import { ImageIcon } from "assets/icons";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

import {
  RECOMMENDATION_REASON_COLORS,
  RECOMMENDATION_REASON_LABELS
} from "../../constants";
import { HydratedRecommendation } from "../../recommendation.types";
import { Badge } from "../Badge";

import styles from "./RecommendationStrip.module.scss";

interface RecommendationStripProps {
  recommendations: HydratedRecommendation[];
  isLoading?: boolean;
  note: string;
  emptyText: string;
}

/**
 * The product page is addressed by category, product and variant together, so
 * a card can only link once the resolver has told us all three. When it has
 * not, the card renders as before rather than as a link that goes nowhere.
 */
const getProductPath = (item: HydratedRecommendation) =>
  item.categoryId && item.productVariantId
    ? `/products/details/${item.categoryId}/${item.productId}/${item.productVariantId}/boxes`
    : null;

const RecommendationStrip = ({
  recommendations,
  isLoading,
  note,
  emptyText
}: RecommendationStripProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={styles.strip}>
        <Skeleton
          count={1}
          height={180}
          width={140}
          borderRadius={14}
          inline
          className={styles.skeleton}
        />
      </div>
    );
  }

  if (!recommendations.length) {
    return <p className={styles.empty}>{emptyText}</p>;
  }

  const renderCardContent = (item: HydratedRecommendation) => (
    <>
      <div className={styles.card__image}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name ?? ""} loading="lazy" />
        ) : (
          <span className={styles.card__placeholder}>
            <ImageIcon width={24} height={24} />
          </span>
        )}
      </div>

      <div className={styles.card__body}>
        <span className={styles.card__name}>
          {item.name ?? t("Unnamed product")}
        </span>

        <Badge
          label={RECOMMENDATION_REASON_LABELS[item.reason] ?? item.reason}
          color={RECOMMENDATION_REASON_COLORS[item.reason] ?? "#8E8E93"}
        />
      </div>
    </>
  );

  return (
    <>
      <p className={styles.note}>{note}</p>

      <div className={styles.strip}>
        {recommendations.map((item) => {
          const path = getProductPath(item);

          return path ? (
            <Link
              key={item.productId}
              to={path}
              className={`${styles.card} ${styles.card_link}`}
            >
              {renderCardContent(item)}
            </Link>
          ) : (
            <div key={item.productId} className={styles.card}>
              {renderCardContent(item)}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default RecommendationStrip;
